import { Icon } from "@msuite/ui/icons";
import { useVirtuoso } from "@msuite/ui/libs/virtuoso";
import { Panels } from "@msuite/ui/panels";
import { useQuery } from "@msuite/ui/query";
import { Timeline } from "@msuite/ui/timeline";
import { responseHandler } from "@msuite/ui/utils";
import { guardArray } from "@msuite/utils/array";
import type {
	ProjectTimelineAssignment,
	ProjectTimelineNoteWithFiles,
	Tables,
} from "@msuite/utils/types";
import { TicketTypeIcon } from "@/components/cards/ticket-card";
import { client } from "@/hono";
import { ProjectTickets } from "./project-tickets";
import { ProjectTimelineAssignmentItem } from "./project-timeline-assignment-item";
import { ProjectTimelineAuditItem } from "./project-timeline-audit-item";
import { ProjectTimelineMenu } from "./project-timeline-menu";
import { ProjectTimelineNoteItem } from "./project-timeline-note-item";

/** Props Interface */
interface ProjectTimelineProps {
	projectId: string;
}

export function ProjectTimeline({ projectId }: ProjectTimelineProps) {
	/** Hooks */
	const virtuosoHandles = useVirtuoso();
	const { data, isLoading, refetch } = useQuery({
		queryKey: ["project", "timeline", projectId],
		queryFn: async () => {
			const response = await client.projects[":projectId"].timeline.$get({
				param: {
					projectId,
				},
			});
			const data = await responseHandler(response);
			if (!data.success) throw new Error("Error fetching timeline");
			return data;
		},
		staleTime: 1,
	});

	/** Render */
	return (
		<Panels>
			<Panels.Panel
				flex={1}
				className="pr-2"
			>
				<ProjectTickets
					projectId={projectId}
					virtuosoHandles={virtuosoHandles}
				/>
			</Panels.Panel>
			<Panels.Panel flex={2}>
				<Timeline
					virtuoso={virtuosoHandles}
					timeline={guardArray(data?.timeline)}
					controls={
						<ProjectTimelineMenu
							projectId={projectId}
							refetch={refetch}
							timeline={guardArray(data?.timeline)}
						/>
					}
					isLoading={isLoading}
					refetch={refetch}
					renderItem={(type, item) => {
						if (type === "assignment") {
							return (
								<ProjectTimelineAssignmentItem
									key={item.id}
									item={item as unknown as ProjectTimelineAssignment}
								/>
							);
						}
						if (type === "note") {
							return (
								<ProjectTimelineNoteItem
									key={item.id}
									item={item as unknown as ProjectTimelineNoteWithFiles}
								/>
							);
						}
						if (type.startsWith("audit")) {
							return (
								<ProjectTimelineAuditItem
									key={item.id}
									type={type}
									item={item as unknown as Tables<"projectsAudit">}
								/>
							);
						}
						return null;
					}}
					renderItemMarker={(type, item) => {
						if (type === "assignment") {
							return (
								<TicketTypeIcon
									ticketTypeId={(item as unknown as ProjectTimelineAssignment).ticket.type}
								/>
							);
						}
						if (type === "note") {
							return (
								<Icon
									name="Text"
									className="stroke-violet-500"
								/>
							);
						}
						if (type === "audit-insert") {
							return (
								<Icon
									name="Sparkles"
									className="stroke-accent-500 translate-y-[0.5px]"
								/>
							);
						}
						if (type === "audit-delete") {
							return (
								<Icon
									name="Trash2"
									className="stroke-red-500"
								/>
							);
						}
						if (type === "audit-merge") {
							return (
								<Icon
									name="Merge"
									className="stroke-orange-500"
								/>
							);
						}
						if (type === "audit-update") {
							return (
								<Icon
									name="Edit"
									className="stroke-amber-500"
								/>
							);
						}
						return null;
					}}
				/>
			</Panels.Panel>
		</Panels>
	);
}
